"use client";

import { useState, useRef, useEffect } from "react";

export interface Position {
    x: number;
    y: number;
}

export interface UseDraggableBubbleOptions {
    onTap: () => void;
}

/**
 * Custom hook providing 60FPS pointer dragging with magnetic edge snapping
 * for floating chat bubbles across desktop, tablet, and mobile devices.
 */
export function useDraggableBubble({ onTap }: UseDraggableBubbleOptions) {
    const [bubblePos, setBubblePos] = useState<Position | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isSnapping, setIsSnapping] = useState(false);

    const dragDataRef = useRef<{
        startX: number;
        startY: number;
        initialX: number;
        initialY: number;
        hasMoved: boolean;
    }>({
        startX: 0,
        startY: 0,
        initialX: 0,
        initialY: 0,
        hasMoved: false,
    });

    // Initialize bubble position safely above scene carousel and docked to the right edge
    useEffect(() => {
        if (typeof window === "undefined") return;

        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        const defaultX = screenWidth - (screenWidth < 640 ? 64 : 72);
        const defaultY = screenHeight - (screenHeight < 640 ? 165 : 180);

        setBubblePos({
            x: Math.max(16, defaultX),
            y: Math.max(70, defaultY)
        });

        const handleResize = () => {
            setBubblePos(prev => {
                if (!prev) return prev;
                const winW = window.innerWidth;
                const winH = window.innerHeight;
                const maxX = winW - (winW < 640 ? 64 : 72);
                const maxY = winH - 120;
                const isRightSide = prev.x + 26 > winW / 2;
                return {
                    x: isRightSide ? maxX : 16,
                    y: Math.max(70, Math.min(maxY, prev.y))
                };
            });
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Pointer Drag Listeners: track window pointermove / pointerup smoothly without losing tracking
    useEffect(() => {
        if (!isDragging) return;

        const onPointerMove = (e: PointerEvent) => {
            const dx = e.clientX - dragDataRef.current.startX;
            const dy = e.clientY - dragDataRef.current.startY;

            if (!dragDataRef.current.hasMoved && Math.hypot(dx, dy) > 5) {
                dragDataRef.current.hasMoved = true;
            }

            if (dragDataRef.current.hasMoved) {
                const nextX = dragDataRef.current.initialX + dx;
                const nextY = dragDataRef.current.initialY + dy;

                const maxX = window.innerWidth - 60;
                const maxY = window.innerHeight - 110;

                setBubblePos({
                    x: Math.max(8, Math.min(maxX, nextX)),
                    y: Math.max(60, Math.min(maxY, nextY)),
                });
            }
        };

        const onPointerUp = (e: PointerEvent) => {
            setIsDragging(false);

            if (dragDataRef.current.hasMoved) {
                // Magnetic Edge Snapping: automatically snaps with spring inertia to nearest screen edge (left or right)
                setIsSnapping(true);
                const screenWidth = window.innerWidth;
                const screenHeight = window.innerHeight;
                const currentX = dragDataRef.current.initialX + (e.clientX - dragDataRef.current.startX);
                const currentY = dragDataRef.current.initialY + (e.clientY - dragDataRef.current.startY);

                const isRight = currentX + 26 > screenWidth / 2;
                const targetX = isRight ? screenWidth - (screenWidth < 640 ? 64 : 72) : 16;
                const targetY = Math.max(70, Math.min(screenHeight - 120, currentY));

                setBubblePos({ x: targetX, y: targetY });
            } else {
                // Tap/Click without movement: Expand into full chat input
                onTap();
            }
        };

        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);
        window.addEventListener("pointercancel", onPointerUp);

        return () => {
            window.removeEventListener("pointermove", onPointerMove);
            window.removeEventListener("pointerup", onPointerUp);
            window.removeEventListener("pointercancel", onPointerUp);
        };
    }, [isDragging, onTap]);

    const handleBubblePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        e.stopPropagation();
        if (!e.isPrimary) return;

        dragDataRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            initialX: bubblePos?.x ?? (window.innerWidth - 70),
            initialY: bubblePos?.y ?? (window.innerHeight - 170),
            hasMoved: false,
        };

        setIsSnapping(false);
        setIsDragging(true);
    };

    return {
        bubblePos,
        isDragging,
        isSnapping,
        handleBubblePointerDown,
    };
}
